import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Loader2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('LoanAutocomplete', 'broker-recon');

/** Quote an array of strings for PostgREST `in` / `not.in` filters.
 *  Escapes embedded backslashes and double-quotes per PostgREST spec. */
export function postgrestInList(values: string[]): string {
  return `(${values.map(v => `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`).join(',')})`;
}

// LE Fee item structure from le_fees JSONB column
export interface LeFeeItem {
  name: string;
  section: string;
  borrowerAmount: number;
  sellerAmount: number;
  paidTo?: string;
  paidToType?: string;
  totalAmount: number;
  aprAffecting?: boolean;
  borrowerPTC?: number;
  borrowerOutsideClosing?: number;
}

// Encompass loan type for broker recon
export interface EncompassLoan {
  id: string;
  encompass_loan_id: string;
  trans_details_loan_number: string | null;
  trans_details_loan_type: string | null;
  borrower_first_name: string | null;
  borrower_last_name: string | null;
  trans_details_loan_amt: number | null;
  trans_details_interest_rate: number | null;
  proposed_pi_payment: number | null;
  loan_term_months: number | null;
  // Interest-only period in months (Encompass field 1177); >0 ⇒ interest-only loan.
  // Optional: shared type is also satisfied by partial DB rows / fixtures elsewhere.
  interest_only_term_months?: number | null;
  loan_officer_id: string | null;
  loan_officer_first_name: string | null;
  loan_officer_last_name: string | null;
  loan_processor_id: string | null;
  loan_processor_first_name: string | null;
  loan_processor_last_name: string | null;
  file_contacts_broker_lender_name: string | null;
  closing_date: string | null;
  funded_date?: string | null;
  milestone: string | null;
  trans_details_loan_status: string | null;
  loan_folder: string | null;
  // CD Fees (Section 800) - For Banker Recon auto-population
  fees_processing: number | null;
  fees_underwriting: number | null;
  fees_prepaid_interest: number | null;
  // Rate Lock - For Banker Recon pricing
  rate_lock_total_buy_price: number | null;
  // LO discount points % (NEWHUD.X1150) — feeds LO Profitability in Banker Recon.
  // Optional: shared type is also satisfied by partial DB rows / fixtures elsewhere.
  discount_point_percent?: number | null;
  // Borrower-paid comp % (origination fee) — feeds Case-2 LO Profitability (no
  // built-in LO-comp margin) in Banker Recon. Optional for the same reason.
  origination_fee_percent?: number | null;
  rate_lock_buy_side_lock_expires: string | null;
  rate_lock_buy_side_adjustments: Array<{
    rate: number;
    description: string;
    priceAdjustmentType: 'BasePrice' | 'ProfitMargin';
  }> | null;
  // Full LE Fees array for Section A dropdown
  le_fees: LeFeeItem[] | null;
  // Rate Lock Loan Program - contains investor name
  rate_lock_loan_program: string | null;
  // Investor (column on encompass_loans). Resolved program-first by
  // resolveActiveLockInvestor at the read path when an active delivery lock
  // is overlaid (via applyDeliveryLockToLoan).
  investor_name: string | null;
  // First payment date (Field 682)
  first_payment_date: string | null;
  // Initial Escrow Deposit (NEWHUD.X1719) - Section 1000 borrower paid
  fees_initial_escrow_deposit: number | null;
  // Lender Credit fields (matches Loan Center / MyCompensationTab)
  trans_details_non_specific_lender_credit: number | null;
  cure_applied_lender_credit: number | null;
  lender_credit_draft_amount: number | null;
  lender_credit_status: string | null;
}

interface LoanAutocompleteProps {
  organizationId: string;
  onSelect: (loan: EncompassLoan) => void;
  selectedLoan?: EncompassLoan | null;
  onClear?: () => void;
  /** Exclude a specific loan from results (e.g., source loan when copying) */
  excludeLoanId?: string;
  /** Exclude multiple loans from results (e.g., loans with pending/completed payouts) */
  excludeLoanIds?: string[];
  /** Exclude loans by loan_number (for manually entered payouts without encompass_loan_id) */
  excludeLoanNumbers?: string[];
  /** If true, searches all channels instead of just 'Brokered' */
  allChannels?: boolean;
  /** Specific channel to filter by (e.g., 'Banked - Retail'). Overrides allChannels when set. */
  channel?: string;
  /** If true, includes loans in 'Completed Loans' folder (needed for broker recon of funded loans) */
  includeCompleted?: boolean;
  /** If true, excludes loans without a loan number */
  requireLoanNumber?: boolean;
}

export function LoanAutocomplete({
  organizationId,
  onSelect,
  selectedLoan,
  onClear,
  excludeLoanId,
  excludeLoanIds,
  excludeLoanNumbers,
  allChannels = false,
  channel,
  includeCompleted = false,
  requireLoanNumber = false
}: LoanAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [loans, setLoans] = useState<EncompassLoan[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasInitiallyLoaded = useRef(false);
  // Request counter to prevent race conditions where an older request's finally block clears loading
  const requestIdRef = useRef(0);

  // Helper to get borrower name - defined early for use in handlers
  const getBorrowerName = useCallback((loan: EncompassLoan) => {
    const firstName = loan.borrower_first_name || '';
    const lastName = loan.borrower_last_name || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown';
  }, []);

  // Search encompass_loans with AbortController for race condition prevention
  const searchLoans = useCallback(async (search: string) => {
    if (!organizationId) {
return;
}

    // Abort any in-flight request to prevent race conditions
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    // Increment request counter to track this specific request
    const thisRequestId = ++requestIdRef.current;

    setLoading(true);
    try {
      let query = supabase
        .from('encompass_loans')
        .select(`
          id,
          encompass_loan_id,
          trans_details_loan_number,
          trans_details_loan_type,
          borrower_first_name,
          borrower_last_name,
          trans_details_loan_amt,
          trans_details_interest_rate,
          proposed_pi_payment,
          loan_term_months,
          interest_only_term_months,
          loan_officer_id,
          loan_officer_first_name,
          loan_officer_last_name,
          loan_processor_id,
          loan_processor_first_name,
          loan_processor_last_name,
          file_contacts_broker_lender_name,
          closing_date,
          funded_date,
          milestone,
          trans_details_loan_status,
          loan_folder,
          fees_processing,
          fees_underwriting,
          fees_prepaid_interest,
          rate_lock_total_buy_price,
          discount_point_percent,
          origination_fee_percent,
          rate_lock_buy_side_lock_expires,
          rate_lock_buy_side_adjustments,
          le_fees,
          rate_lock_loan_program,
          investor_name,
          first_payment_date,
          fees_initial_escrow_deposit,
          trans_details_non_specific_lender_credit,
          cure_applied_lender_credit,
          lender_credit_draft_amount,
          lender_credit_status
        `)
        .eq('organization_id', organizationId)
        .eq('deleted_from_encompass', false)
        .order('last_modified_date', { ascending: false })
        .limit(20)
        .abortSignal(abortControllerRef.current.signal);

      // Filter by channel: specific channel takes precedence, then allChannels, default to 'Brokered'
      if (channel) {
        query = query.eq('channel', channel);
      } else if (!allChannels) {
        query = query.eq('channel', 'Brokered');
      }

      // Exclude specific loan if provided (e.g., source loan when copying)
      if (excludeLoanId) {
        query = query.neq('encompass_loan_id', excludeLoanId);
      }

      // Exclude multiple loans if provided (e.g., loans with pending/completed payouts)
      if (excludeLoanIds && excludeLoanIds.length > 0) {
        query = query.not('encompass_loan_id', 'in', postgrestInList(excludeLoanIds));
      }

      // Exclude loans by loan_number (for manually entered payouts without encompass_loan_id)
      if (excludeLoanNumbers && excludeLoanNumbers.length > 0) {
        query = query.not('trans_details_loan_number', 'in', postgrestInList(excludeLoanNumbers));
      }

      // Exclude loans in completed/archived folders (broker recon needs completed loans since funded loans live there)
      if (includeCompleted) {
        query = query.not('loan_folder', 'in', '((Archive),(Trash),Transferred Data,Adverse Loans)');
      } else {
        query = query.not('loan_folder', 'in', '(Completed Loans,(Archive),(Trash),Transferred Data,Adverse Loans)');
      }

      // Exclude loans without a loan number (e.g., Prospect-stage loans)
      if (requireLoanNumber) {
        query = query.not('trans_details_loan_number', 'is', null);
      }

      // Add search filter if provided
      if (search.trim()) {
        const searchLower = search.toLowerCase().trim();
        query = query.or(
          `borrower_first_name.ilike.%${searchLower}%,borrower_last_name.ilike.%${searchLower}%,trans_details_loan_number.ilike.%${searchLower}%`
        );
      }

      const { data, error } = await query;

      if (error) {
        // Only update state if this is still the current request
        if (thisRequestId === requestIdRef.current) {
          log.error('Error searching encompass loans', { search }, error);
          setLoans([]);
        }
        return;
      }

      // Only update state if this is still the current request
      if (thisRequestId === requestIdRef.current) {
        setLoans(data || []);
        setHighlightedIndex(-1);
      }
    } catch (error) {
      // Silently ignore aborted requests - this is expected behavior
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      // Only log/update if this is still the current request
      if (thisRequestId === requestIdRef.current) {
        log.error('Error searching loans', { search }, error);
        setLoans([]);
      }
    } finally {
      // Only clear loading if this is still the current request
      if (thisRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [organizationId, allChannels, channel, includeCompleted, excludeLoanId, excludeLoanIds, excludeLoanNumbers, requireLoanNumber]);

  // Unified search effect - handles both initial load and debounced search
  useEffect(() => {
    if (!open) {
      // Reset flag when closed
      hasInitiallyLoaded.current = false;
      return;
    }

    // Initial load when dropdown first opens (no debounce)
    if (!hasInitiallyLoaded.current) {
      hasInitiallyLoaded.current = true;
      searchLoans('');
      return;
    }

    // Debounced search when typing (only after initial load)
    const timer = setTimeout(() => {
      searchLoans(inputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [open, inputValue, searchLoans]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[role="option"]');
      const highlightedItem = items[highlightedIndex] as HTMLElement;
      if (highlightedItem) {
        highlightedItem.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleSelect = useCallback((loan: EncompassLoan) => {
    onSelect(loan);
    setOpen(false);
    setInputValue('');
    setHighlightedIndex(-1);
  }, [onSelect]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setInputValue('');
    if (onClear) {
      onClear();
    }
    inputRef.current?.focus();
  }, [onClear]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (!open) {
setOpen(true);
}
  }, [open]);

  const handleInputFocus = useCallback(() => {
    setOpen(true);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < loans.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : loans.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && loans[highlightedIndex]) {
          handleSelect(loans[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        setInputValue('');
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  }, [open, loans, highlightedIndex, handleSelect]);

  const formatCurrency = (amount: number | null | undefined) => {
    if (!amount) {
return '$0';
}
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Display value logic - show inputValue when user is typing (fixes "frozen display" issue)
  // When inputValue is set, prefer it over the selected loan's name
  const displayValue = inputValue || (selectedLoan ? getBorrowerName(selectedLoan) : '');
  const showClearButton = selectedLoan || inputValue;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative w-full">
          {/* Searchable Input Field */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-autocomplete="list"
              aria-controls="loan-listbox"
              aria-activedescendant={highlightedIndex >= 0 ? `loan-option-${highlightedIndex}` : undefined}
              placeholder="Search by borrower name or loan #..."
              value={displayValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              className={cn(
                "pl-9 pr-8",
                selectedLoan && "text-foreground"
              )}
            />
            {showClearButton && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </PopoverAnchor>

      {/* Dropdown Results - Portaled via PopoverContent */}
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Don't close if clicking on the input itself
          if (inputRef.current?.contains(e.target as Node)) {
            e.preventDefault();
          }
        }}
      >
        <div
          ref={listRef}
          role="listbox"
          id="loan-listbox"
          className="max-h-[300px] overflow-y-auto overscroll-contain"
          tabIndex={-1}
          onWheel={(e) => {
            // Ensure wheel events scroll this container, not parent
            e.stopPropagation();
            const target = e.currentTarget;
            target.scrollTop += e.deltaY;
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              <span className="text-sm text-muted-foreground">Searching...</span>
            </div>
          ) : loans.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No loans found.
            </div>
          ) : (
            <div className="p-1">
              {loans.map((loan, index) => (
                <div
                  key={loan.id}
                  id={`loan-option-${index}`}
                  role="option"
                  aria-selected={selectedLoan?.id === loan.id}
                  onClick={() => handleSelect(loan)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={cn(
                    "flex items-center gap-2 px-2 py-2 rounded-sm cursor-pointer",
                    "hover:bg-accent hover:text-accent-foreground",
                    highlightedIndex === index && "bg-accent text-accent-foreground",
                    selectedLoan?.id === loan.id && "bg-accent/50"
                  )}
                >
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0",
                      selectedLoan?.id === loan.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium truncate">
                      {getBorrowerName(loan)}
                    </span>
                    <span className="text-xs text-muted-foreground truncate">
                      {loan.trans_details_loan_number || 'No #'} &bull; {formatCurrency(loan.trans_details_loan_amt)} &bull; {loan.milestone || loan.trans_details_loan_status || 'N/A'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
